import React from 'react';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { messages } from './messages';
import styles from './user-limit-enforcer-modal.module.less';
import { UserDeactivationTable } from '../../../pages/settings/payment-and-subscription/user-deactivation-table';
import Global from '../../../store/global-selectors';
import ModalPortal from '../../modal-portal';

const MODAL_HEADER_ID = 'user-limit-enforcer-modal-header';

/**
 * User Limit Enforcer Modal component
 * @param {object} props
 * @param {boolean} props.canDismiss - Whether user can dismiss modal without resolving (system admins)
 * @param {boolean} props.hasBillingPermissions - Whether user has billing permissions
 * @param {number} props.maxAllowedUsers - Maximum allowed active users
 * @param {() => void} props.onDismiss - Callback when modal is dismissed
 * @returns {React.ReactElement}
 */
export const UserLimitEnforcerModal = ({ canDismiss, hasBillingPermissions, maxAllowedUsers, onDismiss }) => {
  const initialUsers = useSelector(Global.selectSellerActiveUsers);
  const [users, setUsers] = useAsyncSafeState(initialUsers);

  const numberOfActiveUsers = users.filter((user) => user.isActive).length;
  const canContinue = numberOfActiveUsers <= maxAllowedUsers;

  return (
    <ModalPortal hideCloseButton={!canDismiss} onClose={canDismiss ? onDismiss : undefined}>
      <Modal className={styles['modal-base']} modalHeaderId={MODAL_HEADER_ID} size='medium'>
        <ModalHeader id={MODAL_HEADER_ID} preventClose={!canDismiss}>
          <Text textType={TextType.H3} value={messages.userLimitExceededHeader} />
        </ModalHeader>
        <ModalBody className={styles['modal-body']}>
          {hasBillingPermissions ? (
            <>
              <Text textType={TextType.Paragraph} value={messages.deactivateToContinue} />
              <UserDeactivationTable maxAllowedUsers={maxAllowedUsers} setUsers={setUsers} users={users} />
            </>
          ) : (
            <>
              <div className={styles['blocked-message']}>
                <Text textType={TextType.Paragraph} value={messages.contactBillingAdmin} />
              </div>
              <div className={styles['blocked-message']}>
                <Text textType={TextType.Paragraph} value={messages.contactAdministrator} />
              </div>
            </>
          )}
        </ModalBody>
        {hasBillingPermissions ? (
          <ModalFooter>
            <Button disabled={!canContinue} onClick={onDismiss} theme={THEME_CONSTANTS.primary}>
              <Text textType={TextType.Paragraph} value={messages.continueButton} />
            </Button>
          </ModalFooter>
        ) : null}
      </Modal>
    </ModalPortal>
  );
};
